import axios from 'axios';
import { API_URL } from '../config';
import { getGlobalAlertHandler } from './modalBridge';
import { getAuthToken } from './authToken';

export interface FileMissingPayload {
  code: string;
  detail?: string;
}

export interface DownloadFileOptions {
  onSessionExpired?: () => void;
  onError?: (message: string) => void;
  /** Chamado quando o backend devolve 404 com code FILE_MISSING (corpo JSON mesmo com responseType blob). */
  onFileMissing?: (payload: FileMissingPayload) => void | Promise<void>;
}

async function tryParseFileMissingFromError(error: unknown): Promise<FileMissingPayload | null> {
  const err = error as { response?: { status?: number; data?: unknown } };
  if (err.response?.status !== 404) {
    return null;
  }
  const data = err.response.data;
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      const json = JSON.parse(text) as { code?: string; detail?: string };
      if (json && json.code === 'FILE_MISSING') {
        return { code: json.code, detail: typeof json.detail === 'string' ? json.detail : undefined };
      }
    } catch {
      return null;
    }
    return null;
  }
  if (data && typeof data === 'object' && (data as FileMissingPayload).code === 'FILE_MISSING') {
    const d = data as FileMissingPayload;
    return { code: d.code, detail: d.detail };
  }
  return null;
}

/**
 * Normaliza o path armazenado na BD para compor a URL do endpoint de download.
 * Aceita legados: sem barra inicial, URL completa, caminho absoluto com /app/uploads/,
 * ou apenas o nome do ficheiro (uuid.ext).
 */
export const normalizeStoragePathForDownload = (filePath: string): string => {
  let p = (filePath || '').trim().replace(/\\/g, '/');
  if (!p) return p;
  if (p.includes('?')) p = p.split('?', 1)[0];
  if (p.includes('#')) p = p.split('#', 1)[0];
  if (/^https?:\/\//i.test(p)) {
    try {
      p = new URL(p).pathname || '';
    } catch {
      return '/uploads/';
    }
  }
  const marker = '/uploads/';
  const idx = p.indexOf(marker);
  if (idx !== -1) {
    return p.slice(idx);
  }
  if (p.startsWith('uploads/')) {
    return `/${p}`;
  }
  if (p && !p.includes('/')) {
    return `${marker}${p}`;
  }
  if (!p.startsWith('/')) {
    return `/${p}`;
  }
  return p;
};

/**
 * Função helper para fazer download de arquivos com autenticação
 * @param filePath - Caminho do arquivo (ex: /uploads/arquivo.docx)
 * @param filename - Nome do arquivo para download (opcional)
 * @param options - Callbacks opcionais para sessão expirada e erro (evita alert nativo)
 */
export const downloadFile = async (
  filePath: string,
  filename?: string,
  options?: DownloadFileOptions
) => {
  try {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Usuário não autenticado');
    }

    const pathForUrl = normalizeStoragePathForDownload(filePath);
    const response = await axios.get(`${API_URL}/api/attachments/download${pathForUrl}`, {
      headers: {
        Authorization: `Bearer ${token}`
      },
      responseType: 'blob', // Importante para arquivos binários
      // Garantir que os headers sejam acessíveis
      transformResponse: [(data) => data]
    });

    // Obter headers
    const contentType = response.headers['content-type'] || '';
    const contentDisposition = response.headers['content-disposition'] || '';

    // Preservar o MIME original para evitar abrir imagem como texto/binário bruto.
    const blob =
      response.data instanceof Blob
        ? response.data
        : new Blob([response.data], {
            type: contentType || 'application/octet-stream',
          });
    const url = window.URL.createObjectURL(blob);

    // Criar link temporário para download
    const link = document.createElement('a');
    link.href = url;
    
    // Função para extrair filename do Content-Disposition
    const extractFilename = (disposition: string): string | null => {
      if (!disposition) return null;
      
      // Tentar diferentes formatos de Content-Disposition
      // Formato 1: attachment; filename="arquivo.docx"
      // Formato 2: attachment; filename*=UTF-8''arquivo.docx
      // Formato 3: attachment; filename="arquivo.docx"; filename*=UTF-8''arquivo.docx
      
      // Primeiro, tentar pegar o filename*=UTF-8'' (formato com encoding)
      let match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
      if (match) {
        try {
          return decodeURIComponent(match[1]);
        } catch {
          // Se falhar o decode, continuar
        }
      }
      
      // Se não tiver encoding, tentar o formato simples filename="..."
      match = disposition.match(/filename="([^"]+)"/i);
      if (match) {
        return match[1];
      }
      
      // Tentar sem aspas
      match = disposition.match(/filename=([^;]+)/i);
      if (match) {
        return match[1].trim();
      }
      
      return null;
    };
    
    // Determinar o nome do arquivo (prioridade: parâmetro > Content-Disposition)
    let finalFilename: string | undefined = filename;
    
    // Se não tiver filename passado como parâmetro, tentar extrair do Content-Disposition
    if (!finalFilename && contentDisposition) {
      const extractedFilename = extractFilename(contentDisposition);
      if (extractedFilename) {
        finalFilename = extractedFilename;
      }
    }
    
    // Sempre forçar download (incluindo imagens)
    if (!finalFilename) {
      finalFilename = normalizeStoragePathForDownload(filePath).split('/').pop() || 'download';
    }
    link.download = finalFilename;
    
    // Adicionar ao DOM, clicar e remover
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Limpar URL do blob após um tempo
    setTimeout(() => {
      window.URL.revokeObjectURL(url);
    }, 100);
  } catch (error: unknown) {
    console.error('Erro ao fazer download do arquivo:', error);
    const showAlert = getGlobalAlertHandler();
    const err = error as { response?: { status?: number } };

    const fm = await tryParseFileMissingFromError(error);
    if (fm) {
      if (options?.onFileMissing) {
        await options.onFileMissing(fm);
      } else if (showAlert) {
        showAlert({
          title: 'Arquivo indisponível',
          message: fm.detail || 'O ficheiro não foi encontrado no armazenamento.',
          variant: 'warning',
        });
      }
      return;
    }

    if (err.response?.status === 401) {
      if (options?.onSessionExpired) {
        options.onSessionExpired();
      } else if (showAlert) {
        showAlert({
          title: 'Sessão expirada',
          message: 'Por favor, faça login novamente.',
          variant: 'warning',
        });
      }
    } else {
      const message = 'Erro ao fazer download do arquivo. Tente novamente.';
      if (options?.onError) {
        options.onError(message);
      } else if (showAlert) {
        showAlert({
          title: 'Erro no download',
          message,
          variant: 'danger',
        });
      }
    }
    return;
  }
};

/**
 * Baixa path interno (/uploads/…) com Bearer e devolve object URL (blob:).
 * Quem chama deve revogar com URL.revokeObjectURL quando não precisar mais.
 */
export const fetchAuthenticatedObjectUrl = async (
  filePath: string,
): Promise<string> => {
  const token = getAuthToken();
  if (!token) {
    throw new Error('Usuário não autenticado');
  }

  const pathForUrl = normalizeStoragePathForDownload(filePath);
  const response = await axios.get(`${API_URL}/api/attachments/download${pathForUrl}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    responseType: 'blob',
    transformResponse: [(data) => data],
  });

  const contentType = response.headers['content-type'] || 'application/octet-stream';
  const blob =
    response.data instanceof Blob
      ? response.data
      : new Blob([response.data], {
          type: contentType,
        });

  return window.URL.createObjectURL(blob);
};

/**
 * Abre o arquivo em nova aba usando blob autenticado.
 */
export const openFileInNewTab = async (
  filePath: string,
  options?: DownloadFileOptions
) => {
  try {
    const url = await fetchAuthenticatedObjectUrl(filePath);
    const popup = window.open(url, '_blank', 'noopener,noreferrer');
    if (!popup) {
      const fallbackLink = document.createElement('a');
      fallbackLink.href = url;
      fallbackLink.target = '_blank';
      fallbackLink.rel = 'noopener noreferrer';
      document.body.appendChild(fallbackLink);
      fallbackLink.click();
      document.body.removeChild(fallbackLink);
    }

    setTimeout(() => {
      window.URL.revokeObjectURL(url);
    }, 60000);
  } catch (error: unknown) {
    console.error('Erro ao abrir arquivo em nova aba:', error);
    const showAlert = getGlobalAlertHandler();
    const err = error as { response?: { status?: number } };

    const fm = await tryParseFileMissingFromError(error);
    if (fm) {
      if (options?.onFileMissing) {
        await options.onFileMissing(fm);
      } else if (showAlert) {
        showAlert({
          title: 'Arquivo indisponível',
          message: fm.detail || 'O ficheiro não foi encontrado no armazenamento.',
          variant: 'warning',
        });
      }
      return;
    }

    if (err.response?.status === 401) {
      if (options?.onSessionExpired) {
        options.onSessionExpired();
      } else if (showAlert) {
        showAlert({
          title: 'Sessão expirada',
          message: 'Por favor, faça login novamente.',
          variant: 'warning',
        });
      }
    } else {
      const message = 'Erro ao abrir arquivo. Tente novamente.';
      if (options?.onError) {
        options.onError(message);
      } else if (showAlert) {
        showAlert({
          title: 'Erro ao abrir arquivo',
          message,
          variant: 'danger',
        });
      }
    }
  }
};
